import type * as React from 'react';
import {
    type GestureResponderEvent,
    type StyleProp,
    StyleSheet,
    Text,
    type TextStyle,
    View,
    type ViewStyle,
} from 'react-native';

import { Icon } from '@gitbook/spine-icons';
import { type IconDefinition, renderIcon } from '@gitbook/spine-react';

import { font, sizes, spacing } from '../constants';
import { Interactive } from '../Interactive';
import { ThemedStyleSheet, useCurrentTheme } from '../theme';

export type TagSize = 'small' | 'medium' | 'large';
export type TagKind = 'primary' | 'secondary' | 'merge' | 'upgrade' | 'danger' | 'info';

export type TagProps = {
    kind?: TagKind;
    size?: TagSize;
    icon?: IconDefinition;
    deletable?: boolean;
    onPress?: (event: GestureResponderEvent) => void;
    children: React.ReactNode;
    style?: StyleProp<ViewStyle>;
};

const ICON_SIZE: { [key in TagSize]: number } = {
    small: sizes.ICON_100,
    medium: sizes.ICON_300,
    large: sizes.ICON_400,
};

const TEXT_SIZE: { [key in TagSize]: TextStyle } = {
    small: font.Caption,
    medium: font.Label,
    large: font.Body,
};

const TAG_HEIGHT: { [key in TagSize]: number } = {
    small: spacing.large,
    medium: spacing.large + spacing.xxsmall,
    large: spacing.large + spacing.small,
};

const TAG_PADDING: { [key in TagSize]: number } = {
    small: spacing.xxsmall,
    medium: spacing.small,
    large: spacing.small + spacing.xxsmall,
};

const TAG_MARGIN: { [key in TagSize]: number } = {
    small: spacing.xxsmall,
    medium: spacing.small,
    large: spacing.small + spacing.xxsmall,
};

/**
 * Tags represent a set of interactive keywords. Tags can be added or removed.
 */
export function Tag(props: TagProps) {
    const {
        children,
        onPress,
        deletable,
        icon: IconCo,
        size = 'medium',
        kind = 'info',
        style,
    } = props;
    const [, styles] = useCurrentTheme(kindStyles[kind]);

    return (
        <Interactive.TouchableOpacity
            style={[
                staticStyles.tag,
                styles.tag,
                {
                    height: TAG_HEIGHT[size],
                    paddingHorizontal: TAG_PADDING[size],
                },
                style,
            ]}
            onPress={onPress}
        >
            {IconCo ? (
                <Text style={styles.cross}>{renderIcon(IconCo, { size: ICON_SIZE[size] })}</Text>
            ) : null}
            {children ? (
                <View>
                    <Text
                        style={[
                            TEXT_SIZE[size],
                            styles.text,
                            IconCo ? staticStyles.withIcon : undefined,
                        ]}
                    >
                        {children}
                    </Text>
                </View>
            ) : null}

            {deletable ? (
                <View style={{ marginLeft: TAG_MARGIN[size] }}>
                    <Icon.X style={styles.cross} size={ICON_SIZE[size]} />
                </View>
            ) : null}
        </Interactive.TouchableOpacity>
    );
}

Tag.defaultProps = {
    kind: 'secondary',
    size: 'medium',
};

const borderWidth = 1;

const staticStyles = StyleSheet.create({
    tag: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-start',
        borderRadius: sizes.RADIUS_M,
        width: 'fit-content',
        borderWidth,
        textAlignVertical: 'center',
        overflow: 'hidden',
    },
    withIcon: {
        marginLeft: spacing.small,
    },
});

const kindStyles = {
    primary: ThemedStyleSheet.create({
        tag: (theme) => ({
            backgroundColor: theme.colors.primary.main,
            borderColor: theme.colors.primary.main,
        }),
        text: (theme) => ({
            color: theme.colors.primary.textInside,
        }),
        cross: (theme) => ({ color: theme.colors.primary.textInside }),
    }),
    secondary: ThemedStyleSheet.create({
        tag: (theme) => ({
            backgroundColor: theme.colors.background.main,
            borderColor: theme.colors.border.main,
        }),
        text: (theme) => ({
            color: theme.colors.text.mid,
        }),
        cross: (theme) => ({ color: theme.colors.text.mid }),
    }),
    merge: ThemedStyleSheet.create({
        tag: (theme) => ({
            backgroundColor: theme.colors.merge.muted,
            borderColor: theme.colors.merge.muted,
        }),
        text: (theme) => ({
            color: theme.colors.merge.standout,
        }),
        cross: (theme) => ({ color: theme.colors.merge.standout }),
    }),
    upgrade: ThemedStyleSheet.create({
        tag: (theme) => ({
            backgroundColor: theme.colors.upgrade.muted,
            borderColor: theme.colors.upgrade.muted,
        }),
        text: (theme) => ({
            color: theme.colors.upgrade.standout,
        }),
        cross: (theme) => ({ color: theme.colors.upgrade.standout }),
    }),
    info: ThemedStyleSheet.create({
        tag: (theme) => ({
            backgroundColor: theme.colors.info.muted,
            borderColor: theme.colors.info.muted,
        }),
        text: (theme) => ({
            color: theme.colors.info.standout,
        }),
        cross: (theme) => ({ color: theme.colors.info.standout }),
    }),
    danger: ThemedStyleSheet.create({
        tag: (theme) => ({
            backgroundColor: theme.colors.danger.muted,
            borderColor: theme.colors.danger.muted,
        }),
        text: (theme) => ({
            color: theme.colors.danger.standout,
        }),
        cross: (theme) => ({ color: theme.colors.danger.standout }),
    }),
};
